/**
 * setupAccess — THE single source of truth for setup GATING: what a MASTER renders vs what a LOCAL admin
 * renders, per field, across every gated setup menu (Customer, Site, Device, Camera). This is the one place
 * a field's disposition is declared; every render routes the decision through `resolveDisp` (setupCommon),
 * the one gate, reading the access from here.
 *
 * RULES (David 2026-08-27, HARD):
 *   - A field with NO entry here resolves to the DEFAULT: master = edit, local = readonly.
 *   - The only setup menus the gate does NOT apply to are **User** and **Display** — those are fully
 *     editable/shown and must never be gated through this.
 *   - To change what master or local sees for any field, you change it HERE (or in the config-param field
 *     schemas in configParser, which are the same mechanism) — never in render code.
 *
 * Each value is an access spec understood by resolveDisp: a shorthand string 'edit' | 'readonly' | 'hidden',
 * or a full per-role pair { master, local } with each slot one of those three. Keys are '<menu>.<field>'.
 *
 * NOTE ON THE DEVICE FACTS BELOW: Device ID / Backend ID / IP / Last Event / Cameras are device-REPORTED
 * metadata and Config Format/revision + the Serial summary are read-only glances — none of them has a
 * config WRITE PATH (the device owns them / they're a summary), so they are declared `readonly`. That is a
 * real constraint, not a preference: a master literally cannot write the device's own reported IP. If a
 * write path is ever added for one, flip its entry here (and wire the edit) and it becomes editable.
 */
import { DEFAULT_ACCESS } from './setupCommon';

export const SETUP_ACCESS = Object.freeze({
  // Applied from the #86bbm6j8w gating form (David). DEFAULT (master:edit / local:readonly) is implicit:
  // only NON-default fields appear below; any key absent here is master-editable and local-readonly.
  // THE single source — to change gating, edit an entry here (or add a new '<menu>.<field>').

  // ── Site ── name + timezone are editable by a non-master site-admin (David: admin + site access
  // can admin sites). Customer assignment + Data Plan stay Dividia-only via the DEFAULT (local:readonly,
  // no entry needed). reportErrors/fileDir/domain stay hidden for local as before.
  'site.name':         { master: 'edit', local: 'edit' },
  'site.timezone':     { master: 'edit', local: 'edit' },
  'site.reportErrors': { master: 'edit', local: 'hidden' },
  'site.fileDir':      { master: 'readonly', local: 'hidden' },
  'site.domain':       { master: 'readonly', local: 'hidden' },

  // ── Device — General identity ──
  // Name: a non-master admin who administers the device may rename it (enforced server-side in
  // modifyOrCreateDevice). Customer + Site stay master-only (default local:readonly) — moving a device
  // between sites/customers is fleet provisioning; scoped site moves for site-admins are a Sprint-34 card.
  'device.name':         { master: 'edit', local: 'edit' },

  // ── Device — General (device-reported / derived) ──
  'device.serial':       { master: 'readonly', local: 'readonly' },
  'device.deviceId':     { master: 'readonly', local: 'readonly' },
  'device.backendId':    { master: 'readonly', local: 'readonly' },
  'device.ip':           { master: 'readonly', local: 'readonly' },
  'device.lastEvent':    { master: 'readonly', local: 'readonly' },
  'device.cameras':      { master: 'readonly', local: 'readonly' },
  'device.configFormat': { master: 'readonly', local: 'hidden' },  // master sees it read-only; hidden from a non-master admin
  'device.confRev':      { master: 'readonly', local: 'hidden' },  // (same) — Config Format + revision are Dividia-facing

  // ── Device — Config / connector [global] ──
  // Device OS / recording timezone (dragon [global].timezone). Editable by master AND a non-master
  // admin for the site/device; the cloudapi config-write path authorizes it per-device (localDeviceChangeAllowed).
  'global.timezone':     { master: 'edit', local: 'edit' },
  'global.use_backend':  { master: 'edit', local: 'hidden' },
  'global.pose_enabled': { master: 'edit', local: 'hidden' },
  'global.record_pro2':  { master: 'edit', local: 'hidden' },
  'global.thumb_width':  { master: 'edit', local: 'hidden' },
  'global.thumb_delay':  { master: 'edit', local: 'hidden' },
  'global.use_b2':       { master: 'edit', local: 'hidden' },
  'global.conf_rev':     { master: 'readonly', local: 'hidden' },
  'global.jpeg_quality': { master: 'edit', local: 'hidden' },
  'global.tmpdir':       { master: 'edit', local: 'hidden' },
  'global.dbfile':       { master: 'edit', local: 'hidden' },
  'global.serial':       { master: 'readonly', local: 'hidden' },

  // ── Device — Config [server] ──
  'server.require_auth':  { master: 'edit', local: 'hidden' },
  'server.https_enabled': { master: 'edit', local: 'hidden' },
  'server.http_port':     { master: 'edit', local: 'hidden' },
  'server.https_port':    { master: 'edit', local: 'hidden' },
  'server.cert_file':     { master: 'edit', local: 'hidden' },
  'server.key_file':      { master: 'edit', local: 'hidden' },

  // ── Device — Config [llm] ──
  'llm.enabled':      { master: 'edit', local: 'hidden' },
  'llm.scene_prompt': { master: 'edit', local: 'edit' },
  'llm.backend':      { master: 'edit', local: 'hidden' },
  'llm.host':         { master: 'edit', local: 'hidden' },
  'llm.port':         { master: 'edit', local: 'hidden' },
  'llm.model':        { master: 'edit', local: 'hidden' },

  // ── Device — Config [mpengine] ──
  'mpengine.enabled': { master: 'edit', local: 'hidden' },
  'mpengine.host':    { master: 'edit', local: 'hidden' },
  'mpengine.port':    { master: 'edit', local: 'hidden' },

  // ── Device — Config [videostore] ──
  'videostore.vs1': { master: 'edit', local: 'hidden' },
  'videostore.vs2': { master: 'edit', local: 'hidden' },

  // ── Device — Config [libaiengine] (legacy) ──
  'libaiengine.obj_names':   { master: 'edit', local: 'hidden' },
  'libaiengine.obj_config':  { master: 'edit', local: 'hidden' },
  'libaiengine.obj_weights': { master: 'edit', local: 'hidden' },

  // ── Device — POS connector [rpc] ──
  'rpc.bind': { master: 'edit', local: 'hidden' },
  'rpc.port': { master: 'edit', local: 'hidden' },

  // ── Device — POS connector [dragon] ──
  'dragon.host':             { master: 'edit', local: 'hidden' },
  'dragon.port':             { master: 'edit', local: 'hidden' },
  'dragon.user':             { master: 'edit', local: 'hidden' },
  'dragon.snapshot_profile': { master: 'edit', local: 'hidden' },
  'dragon.meta_profile':     { master: 'edit', local: 'hidden' },
  'dragon.conf':             { master: 'edit', local: 'hidden' },

  // ── Device — POS connector [cloud] ──
  'cloud.enabled':   { master: 'edit', local: 'hidden' },
  'cloud.api':       { master: 'readonly', local: 'hidden' },
  'cloud.ws':        { master: 'readonly', local: 'hidden' },
  'cloud.api_token': { master: 'readonly', local: 'hidden' },

  // ── Device — POS source fields (type-agnostic: gates the field across LPR / Scale / PSCL) ──
  'pos.name':                       { master: 'edit', local: 'edit' },
  'pos.tcp_port':                   { master: 'edit', local: 'edit' },
  'pos.min_confidence':             { master: 'edit', local: 'edit' },
  'pos.format':                     { master: 'edit', local: 'edit' },
  'pos.cameras':                    { master: 'edit', local: 'edit' },
  'pos.ticket_source':              { master: 'edit', local: 'edit' },
  'pos.min_trans_duration':         { master: 'edit', local: 'edit' },
  'pos.hung_trans_timeout':         { master: 'edit', local: 'edit' },
  'pos.keep':                       { master: 'edit', local: 'edit' },
  'pos.ticket_threshold':           { master: 'edit', local: 'edit' },
  'pos.file_format':                { master: 'edit', local: 'edit' },
  'pos.file_format_inbound':        { master: 'edit', local: 'edit' },
  'pos.combine_file_type':          { master: 'edit', local: 'edit' },
  'pos.type':                       { master: 'edit', local: 'edit' },
  'pos.ticket_print_delay':         { master: 'edit', local: 'hidden' },
  'pos.start_transaction':          { master: 'edit', local: 'edit' },
  'pos.finish_transaction':         { master: 'edit', local: 'edit' },
  'pos.images':                     { master: 'edit', local: 'edit' },
  // Facility Tracker (type 20): both master AND local edit EVERY field (name/images are edit/edit above;
  // these two expiry windows match). (David 2026-08-30)
  'pos.expire_facility_in_timeout':       { master: 'edit', local: 'edit' },
  'pos.expire_truck_transaction_timeout': { master: 'edit', local: 'edit' },
  'pos.custom_stx':                 { master: 'edit', local: 'hidden' },
  'pos.custom_offset':              { master: 'edit', local: 'hidden' },
  'pos.custom_length':              { master: 'edit', local: 'hidden' },
  'pos.custom_implied_decimal':     { master: 'edit', local: 'hidden' },
  'pos.custom_precision':           { master: 'edit', local: 'hidden' },
  'pos.custom_unit':                { master: 'edit', local: 'hidden' },
  'pos.ip':                         { master: 'edit', local: 'edit' },
  'pos.port':                       { master: 'edit', local: 'edit' },
  'pos.state_change_limit':         { master: 'edit', local: 'edit' },
  'pos.sensitivity':                { master: 'edit', local: 'edit' },
  'pos.truck_enter_min_weight':     { master: 'edit', local: 'edit' },
  'pos.truck_leaving_max_weight':   { master: 'edit', local: 'edit' },
  'pos.weight_decrease_limit':      { master: 'edit', local: 'edit' },
  'pos.tare_minimum':               { master: 'edit', local: 'edit' },
  'pos.tare_limit':                 { master: 'edit', local: 'edit' },
  'pos.weight_diff_limit':          { master: 'edit', local: 'edit' },
  'pos.overweight_limit':           { master: 'edit', local: 'edit' },
  'pos.weight_field':               { master: 'edit', local: 'edit' },
  'pos.report_format':              { master: 'edit', local: 'edit' },
  'pos.min_net_percent_of_tare':    { master: 'edit', local: 'edit' },
  'pos.start_transaction_inbound':  { master: 'edit', local: 'edit' },
  'pos.finish_transaction_inbound': { master: 'edit', local: 'edit' },
  'pos.images_inbound':             { master: 'edit', local: 'edit' },
  'pos.timeout_inbound':            { master: 'edit', local: 'edit' },
  'pos.match_inbound':              { master: 'edit', local: 'edit' },
  'pos.tare_field':                 { master: 'edit', local: 'edit' },
  'pos.tare_value':                 { master: 'edit', local: 'edit' },
  'pos.pair_timeout':               { master: 'edit', local: 'edit' },
  'pos.rabbit_host':                { master: 'edit', local: 'edit' },
  'pos.rabbit_port':                { master: 'edit', local: 'edit' },
  'pos.rabbit_user':                { master: 'edit', local: 'edit' },
  'pos.rabbit_pass':                { master: 'edit', local: 'edit' },
  'pos.rabbit_virtual_host':        { master: 'edit', local: 'edit' },
  'pos.rabbit_exchange':            { master: 'edit', local: 'edit' },
  'pos.rabbit_queue':               { master: 'edit', local: 'edit' },
  'pos.rabbit_routing_key':         { master: 'edit', local: 'edit' },

  // ── Camera ──
  'camera.name':            { master: 'edit', local: 'edit' },
  'camera.streamId':        { master: 'readonly', local: 'readonly' },
  'camera.enabled':         { master: 'edit', local: 'edit' },
  'camera.usePose':         { master: 'edit', local: 'hidden' },
  'camera.recUrl':          { master: 'edit', local: 'edit' },
  'camera.liveUrl':         { master: 'edit', local: 'edit' },
  'camera.snapshotUrl':     { master: 'readonly', local: 'readonly' },
  'camera.objectOnly':      { master: 'edit', local: 'readonly' },
  'camera.motionSens':      { master: 'edit', local: 'edit' },
  'camera.motionPost':      { master: 'edit', local: 'edit' },
  'camera.stillInterval':   { master: 'edit', local: 'edit' },
  'camera.motionMask':      { master: 'edit', local: 'edit' },
  'camera.mpe':             { master: 'edit', local: 'hidden' },
  'camera.useOnnx':         { master: 'edit', local: 'hidden' },
  'camera.onvifUseTracker': { master: 'edit', local: 'hidden' },
  'camera.lprMotionSens':   { master: 'edit', local: 'edit' },
  'camera.lprMotionPost':   { master: 'edit', local: 'edit' },
  'camera.lprRoi':          { master: 'edit', local: 'edit' },
  'camera.ptz':             { master: 'edit', local: 'edit' },
});

/**
 * The access spec for a setup field key, from the single source. Unknown key → DEFAULT (master:edit,
 * local:readonly). Pass the result straight to resolveDisp / AccessField.
 * @param {string} key - '<menu>.<field>' (e.g. 'device.deviceId')
 * @returns {(string|{master:string, local:string})}
 */
export const fieldAccess = (key) =>
  (key && Object.prototype.hasOwnProperty.call(SETUP_ACCESS, key)) ? SETUP_ACCESS[key] : DEFAULT_ACCESS;
